import styled from "@emotion/styled";
import { Box, Tab, Tabs } from "@mui/material";
import { buildSchemeAwareChipStyles } from "@themes/utils/gradients";

/**
 * In the top bar each control draws its own flat, hairline box. Sizes come
 * from `theme.controls` and `shape.borderRadiusButton`.
 */
/** The search field's width in the bar, where it stays open. */
export const TOP_BAR_SEARCH_WIDTH = 220;
/** Between one control and the next. */
export const TOP_BAR_GROUP_GAP = 6;

export const HeaderButtonsGroup = styled(Box)(({ theme }) => ({
  // On a page the group is one bordered control, so its children fill the
  // height inside the border.
  '&:not([data-in-top-bar="true"]) .MuiButtonBase-root, &:not([data-in-top-bar="true"]) .MuiFormControl-root':
    {
      height: theme.controls.height - 2,
      minHeight: theme.controls.height - 2
    },
  // A shut search stays square inside the border.
  '&:not([data-in-top-bar="true"]) .search-container:not([data-expanded="true"])':
    {
      width: theme.controls.height - 2
    },
  // In the bar the group is only a row — each control draws its own box.
  '&[data-in-top-bar="true"]': {
    background: "none",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    boxSizing: "border-box",
    gap: TOP_BAR_GROUP_GAP,
    height: theme.controls.height,
    padding: 0
  },
  // The glass surface is painted on ::before/::after by `getButtonStyles`;
  // the bar's controls are flat, so those layers go with it.
  '&[data-in-top-bar="true"]::before, &[data-in-top-bar="true"]::after': {
    display: "none"
  },
  alignItems: "center",
  // Flat: a hairline box, not the glass surface this group used to paint.
  border: `1px solid ${theme.vars.palette.border.subtle}`,
  borderRadius: theme.shape.borderRadiusButton,
  boxSizing: "border-box",
  display: "flex",
  height: theme.controls.height,
  overflowX: "visible",
  overflowY: "hidden"
}));

export const HeaderButtonSlot = styled(Box)(({ theme }) => {
  return {
    // The divider belongs to the segmented page-header group; in the bar the
    "&::after": {
      backgroundColor: theme.vars.palette.border.strong,
      content: '""',
      height: "25px",
      opacity: 0.6,
      width: "1px"
    },
    "&:last-of-type::after": {
      display: "none"
    },
    '&[data-disabled="true"]': {
      opacity: theme.vars.appEffects.opacity.disabled
    },
    '&[data-filter-active="true"] .MuiButtonBase-root': {
      outline: `2px solid var(--mui-palette-primary-glow)`,
      outlineOffset: -2
    },
    // controls are separate boxes with nothing to divide.
    '[data-in-top-bar="true"] &::after': {
      display: "none"
    },
    alignItems: "center",
    display: "flex",
    position: "relative"
  };
});

export const StyledHeaderTabs = styled(Tabs)(({ theme }) => {
  return {
    "& .MuiTabs-scrollableX": {
      maxWidth: "100%"
    },
    "& .MuiTabs-scrollButtons.Mui-disabled": {
      opacity: 0.3
    },
    "& .MuiTabs-scroller": {
      maxWidth: "100%",
      overflowX: "auto",
      overflowY: "hidden"
    },
    ".MuiTabs-indicator": {
      backgroundColor: `${theme.vars.palette.action.selected} !important`
    },
    ".MuiTabs-list": {
      gap: 12
    },
    background: "transparent",
    borderBottom: `1px solid ${theme.vars.palette.border.subtle}`,
    flex: "1 1 auto",
    maxWidth: "100%",
    minHeight: "auto",
    minWidth: 0,
    overflow: "hidden",
    width: "100%"
  };
});

export const HeaderTab = styled(Tab, {
  shouldForwardProp: (prop) => prop !== "tabWidth" && prop !== "truncateLabel"
})<{
  tabWidth?: number;
  truncateLabel?: boolean;
}>(({ tabWidth, theme, truncateLabel }) => {
  return {
    "& span": {
      background: theme.vars.palette.surface.inset,
      borderRadius: 30,
      color: theme.vars.palette.text.tertiary
    },
    ...(truncateLabel && {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    }),
    "&.Mui-selected": {
      color: theme.vars.palette.action.selected
    },
    "&.Mui-selected .MuiTab-iconWrapper": {
      border: `1px solid ${theme.vars.palette.action.selected}`
    },
    "&.MuiButtonBase-root.MuiTab-root > .MuiTab-iconWrapper": {
      marginLeft: "0 !important"
    },
    color: theme.vars.palette.text.secondary,
    display: "flex",
    flexDirection: "row",
    gap: 8,
    maxWidth: tabWidth ?? "auto",
    minHeight: "auto",
    minWidth: "auto",
    padding: "0px 4px 12px 4px",
    textTransform: "capitalize",
    width: "auto"
  };
});

export const TabCount = styled("div")(({ theme }) => {
  return {
    ...buildSchemeAwareChipStyles(theme, theme.vars.palette.surface.base),
    alignItems: "center",
    border: `1px solid ${theme.vars.palette.border.subtle}`,
    borderRadius: theme.shape.borderRadius,
    color: "inherit !important",
    display: "flex",
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    justifyContent: "center",
    minWidth: "30px",
    padding: "0 4px"
  };
});
